'use client';

import { LoadingFallback } from '@order-up/common';
import type { ILocation, ITenant } from '@order-up/models';
import { asTenantDocId } from '@order-up/models';
import { useEffect, useRef, useState } from 'react';
import { useSelection } from '../contexts/SelectionContext';
import { useAuth } from '../hooks/useAuth';
import { useMeLocations } from '../hooks/useMeLocations';
import { useGetLocationsByTenantId } from '../hooks/useLocations';
import { useSupportLandingPending } from '../hooks/useSupportLanding';
import { useGetTenantsByUserId } from '../hooks/useTenants';
import { useGetUserByUid } from '../hooks/useUsers';
import type { SelectionData, SelectionRequirements } from '../types/routes';
import { createClientLogger } from '../utils/logger';
import { SelectionWizard, SelectionWizardProps } from './SelectionWizard';
import { SuspendedAccountMessage } from './SuspendedAccountMessage';

const logger = createClientLogger('AuthenticatedSelectionGuard');

/** How long auto-selection may stay pending before the wizard is shown instead. */
const AUTO_SELECT_WATCHDOG_MS = 5000;

type TimerRef = { current: ReturnType<typeof setTimeout> | null };

/** Cancel the timer a ref holds, if any, and forget its id. */
function cancelTimer(ref: TimerRef): void {
  if (ref.current) {
    clearTimeout(ref.current);
    ref.current = null;
  }
}

interface AuthenticatedSelectionGuardProps {
  children: React.ReactNode | ((data: SelectionData) => React.ReactNode);
  requireSelection: SelectionRequirements;
  requireAuth?: boolean;
  selectionWizardProps?: Partial<SelectionWizardProps>;
  onSelectionComplete?: (data: SelectionData) => void;
  loadingComponent?: React.ComponentType;
}

/**
 * Handles selection requirements for authenticated apps.
 *
 * This component:
 * 1. Checks if selection is already persisted
 * 2. If not, shows SelectionWizard
 * 3. Handles selection completion and persistence
 * 4. Renders children when selection is complete
 */
export function AuthenticatedSelectionGuard({
  children,
  requireSelection,
  requireAuth = true,
  selectionWizardProps,
  onSelectionComplete,
  loadingComponent: LoadingComponent = LoadingFallback
}: AuthenticatedSelectionGuardProps) {
  const [showWizard, setShowWizard] = useState(false);
  const [selectionData, setSelectionData] = useState<SelectionData | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAutoSelecting, setIsAutoSelecting] = useState(false);
  const hasProcessedAutoSelection = useRef(false);
  const [autoSelectTimeout, setAutoSelectTimeout] = useState(false);
  /**
   * The 100ms COMPLETION timer on the seamless auto-select path. Not to be
   * confused with the 5-SECOND WATCHDOG ("auto-selection did not finish, show
   * the wizard"), which owns no ref at all — its effect below holds the id in
   * its own closure. Different timers with opposite meanings.
   */
  const autoSelectCompleteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get auth and data for auto-selection logic
  const { data: authData } = useAuth();
  const { authUser } = authData || {};
  const persistedSelection = useSelection();

  // An admin support-scope landing is establishing a DIFFERENT identity's scope
  // in this tab right now (see ../utils/supportLanding).
  //
  // Every decision below is derived from two inputs that are both still the
  // PRE-support values during that window: the persisted selection (the admin's
  // own last restaurant, stamped with their uid so it reads as trustworthy) and
  // `/me/locations` (the admin's own memberships, which by definition do not
  // contain the support tenant). Acting on them meant this guard would paint the
  // wrong restaurant, then DELETE the selection the landing had just seeded as
  // "not one of your tenants", then auto-select the admin's own tenant in its
  // place — a race the landing lost most of the time. Whether the support
  // session worked came down to which promise resolved first.
  //
  // So: hold. Holding renders the loading component and nothing else, grants no
  // access, and is bounded by the landing's own watchdog.
  const supportLandingPending = useSupportLandingPending();

  // ONE server call that already answers "which (tenant, restaurant) pairs can
  // this user reach?" — `/users/me/locations` collapses the
  // assignments → tenant-guid → restaurants-by-tenant chain server-side.
  //
  // This replaces a two-stage client waterfall on the boot path
  // (`GET /users/me/assignments`, then N parallel `tenants/{docId}` Firestore
  // reads inside `getTenantsByUserId`) that ran on EVERY load — including for a
  // returning user whose persisted selection was already valid. Management's
  // ManagementAccessGate already issues this exact query, so for that app it is
  // a React Query cache hit and costs nothing at all.
  const { data: meLocations, isLoading: isMeLocationsLoading } = useMeLocations();

  // A persisted selection is only trustworthy if its tenant belongs to the
  // currently authenticated user. Stale localStorage from a previous session on
  // the same browser (e.g. onboarding signs a different user in and bounces them
  // to management via a full-page redirect, bypassing management's
  // logout-clears-selection path) would otherwise render another customer's
  // tenant in the sidebar and trip server-side BOOKS_ACCESS_DENIED.
  const persistedTenantId =
    persistedSelection?.selection.tenant?.tenantId ??
    persistedSelection?.selection.tenantId ??
    null;
  // The persisted selection carries the uid of whoever made it. When that is
  // the currently signed-in user, the "is this someone else's leftover
  // selection?" question is already answered locally — no round-trip needed.
  // Legacy selections written before the stamp existed have `ownerUid === null`
  // and fall through to the server check exactly as before.
  const selectionOwnedByCurrentUser = Boolean(
    authUser?.uid &&
    persistedSelection?.ownerUid &&
    persistedSelection.ownerUid === authUser.uid,
  );

  // Server-confirmed membership. Still authoritative for revocation — it just
  // no longer has to complete before we can paint for a user whose own stamp
  // we recognise.
  const serverConfirmsTenantMembership =
    persistedTenantId !== null &&
    !isMeLocationsLoading &&
    Array.isArray(meLocations) &&
    meLocations.some((r) => r.tenantId === persistedTenantId);

  const persistedTenantBelongsToUser =
    persistedTenantId !== null &&
    (selectionOwnedByCurrentUser || serverConfirmsTenantMembership);

  // Restaurant-level authorization (defense-in-depth, mirrors the tenant check
  // above). The tenant check alone still leaks a *different* restaurant under a
  // shared tenant: e.g. the previous user was staff at restaurant A, the current
  // user is a member of the same tenant but only assigned to restaurant B.
  //
  // `/me/locations` has already expanded grants to concrete reachable
  // restaurants server-side (tenant-scoped grants fan out to every restaurant
  // under the tenant; restaurant-scoped grants contribute their one row), so
  // reachability is a simple membership test against rows rather than a
  // re-implementation of the scope rules on the client. We only ever treat
  // access as PROVEN-DENIED once a non-empty row set has loaded and both docIds
  // are known — otherwise we fail open and defer to the server boundary
  // (BOOKS_ACCESS_DENIED), never hiding the app on a transient/partial load.
  const persistedTenantDocId = persistedSelection?.selection.tenant?.docId ?? null;
  const persistedLocationDocId = persistedSelection?.selection.restaurant?.docId ?? null;
  const persistedLocationAccessProvenDenied = Boolean(
    requireSelection.restaurant &&
    persistedTenantBelongsToUser &&
    Array.isArray(meLocations) && meLocations.length > 0 &&
    persistedTenantDocId !== null &&
    persistedLocationDocId !== null &&
    !meLocations.some(
      (r) =>
        r.tenantDocId === persistedTenantDocId &&
        r.restaurantDocId === persistedLocationDocId,
    ),
  );

  // The persisted selection is fully vouched for — the heavy wizard/auto-select
  // data below is not needed at all. Gating on this keeps a returning user's
  // boot down to the single `/me/locations` call above. If the selection later
  // proves stale and is cleared, this flips false and the queries re-enable on
  // the next render, so auto-select still gets its data.
  const hasTrustedPersistedSelection = Boolean(
    persistedSelection?.hasValidSelection &&
    persistedTenantBelongsToUser &&
    !persistedLocationAccessProvenDenied,
  );

  // Full tenant documents, needed ONLY to drive auto-selection and the wizard.
  const { data: userTenants, isLoading: isTenantsLoading } = useGetTenantsByUserId(
    hasTrustedPersistedSelection ? '' : (authUser?.uid || '')
  );
  // Only fetch tenant[0]'s FULL restaurants list when it might actually be used
  // (auto-select / wizard). Passing 0 disables the query (enabled: !!tenantId).
  const { data: locations, isLoading: isLocationsLoading } = useGetLocationsByTenantId(
    hasTrustedPersistedSelection ? 0 : (userTenants?.[0]?.tenantId || 0)
  );

  // Fetch user document to check for suspended assignments. Not on the critical
  // path — only read on the wizard branch.
  const { data: firestoreUser } = useGetUserByUid(authUser?.uid);
  const hasSuspendedAssignments = firestoreUser?.assignments?.some(
    (a) => a.status === 'suspended',
  ) ?? false;

  // Check if we have valid selection based on requirements. `selectionData`
  // (chosen this session via auto-select/wizard) is always trusted; a persisted
  // selection is trusted only once we've confirmed both its tenant AND its
  // restaurant belong to this user, so we never render tenant- or
  // restaurant-scoped UI for something the user can't reach. While tenants are
  // still loading we treat it as not-yet-valid (shows the loading state, not a
  // wizard) to avoid a flash of the wrong tenant.
  const hasValidSelection = Boolean(selectionData ||
    (persistedSelection?.hasValidSelection &&
      (!requireSelection.tenant || persistedSelection.selection.tenant) &&
      (!requireSelection.restaurant ||
        (persistedSelection.selection.restaurant && !persistedLocationAccessProvenDenied)) &&
      persistedTenantBelongsToUser));

  // Waiting to auto-select, with nothing else on screen: one tenant, a
  // selection that needs a restaurant, no wizard, no selection yet, and no
  // auto-selection under way — typically because that tenant's restaurants have
  // not finished loading. This is exactly the state in which the guard renders
  // its loader and relies on something ELSE to move it on; a support landing is
  // excluded because it holds the loader under its own watchdog.
  const isAwaitingAutoSelect = Boolean(
    !supportLandingPending &&
    isInitialized &&
    !showWizard &&
    !hasValidSelection &&
    !isAutoSelecting &&
    userTenants?.length === 1 &&
    requireSelection.tenant &&
    requireSelection.restaurant,
  );

  // The watchdog: if auto-selection is still pending after 5s, show the wizard
  // rather than hold the loader forever.
  //
  // Keyed on the STALL CONDITION, not scheduled from the auto-selection effect.
  // It used to be scheduled there and cancelled by that effect's per-run
  // cleanup — but arming it also set `isInitialized`, a dependency of that
  // effect, so the re-run it caused cancelled it on the spot. It never fired.
  // Here its lifetime is exactly the stall's: auto-selection starting or
  // succeeding, the wizard appearing by any route, or unmount each flip the
  // condition (or tear the component down) and the cleanup cancels the timer;
  // nothing unrelated re-runs this effect. Pinned by
  // test/components/selectionGuardAutoSelectTimers.test.tsx.
  useEffect(() => {
    if (!isAwaitingAutoSelect) return;
    const watchdog = setTimeout(() => {
      logger.debug('Auto-selection timeout - showing wizard');
      setAutoSelectTimeout(true);
      setShowWizard(true);
    }, AUTO_SELECT_WATCHDOG_MS);
    return () => clearTimeout(watchdog);
  }, [isAwaitingAutoSelect]);

  // Cancel the auto-select completion timer on UNMOUNT — and only then.
  //
  // This cannot live in the auto-selection effect's own cleanup. React runs an
  // effect's cleanup before EVERY re-run, not just on unmount, and scheduling
  // the completion timer flips `isAutoSelecting` — a dependency of that effect
  // — so it re-runs at once. A cleanup returned from the scheduling branch
  // therefore cancels the completion timer before it can fire, and the guard
  // holds its loader forever. The branch used to end in a bare `return;`
  // instead, which registered no cleanup at all: the timer outlived the
  // component and fired against a dead tree (under vitest an unhandled
  // `ReferenceError: window is not defined` from react-dom that failed the
  // whole @order-up/client run with every test passing; in the browser a state
  // update on an unmounted component). Pinned, both ways, by
  // test/components/selectionGuardAutoSelectTimers.test.tsx.
  useEffect(
    () => () => {
      cancelTimer(autoSelectCompleteTimeoutRef);
    },
    [],
  );

  // Auto-selection logic - seamlessly select if only one option available
  useEffect(() => {
    logger.debug('useEffect triggered', {
      isInitialized,
      hasValidSelection,
      isAutoSelecting,
      isTenantsLoading,
      isRestaurantsLoading: isLocationsLoading,
      userTenantsLength: userTenants?.length,
      restaurantsLength: locations?.length,
      persistedHasValidSelection: persistedSelection?.hasValidSelection
    });

    // A support landing owns the selection for this tab until it settles.
    // Auto-selecting from the ADMIN's own tenants here would both race the
    // landing and persist the wrong restaurant under their uid, where it then
    // looks like a legitimately owned selection on every later load.
    if (supportLandingPending) {
      logger.debug('Skipping effect - support landing in progress');
      return;
    }

    if (isAutoSelecting) {
      logger.debug('Skipping effect - auto-selecting in progress');
      return;
    }

    // If we already have a valid selection, initialize immediately
    if (hasValidSelection && !isInitialized) {
      logger.debug('Found valid persisted selection, initializing immediately');
      setShowWizard(false);
      setIsInitialized(true);
      return;
    }

    // Skip if still loading data for auto-selection logic
    if (isTenantsLoading) {
      logger.debug('Still loading tenants, waiting...');
      return;
    }

    // Can we auto-select everything without showing wizard?
    const canAutoSelect = userTenants?.length === 1 &&
      locations?.length === 1 &&
      !isLocationsLoading &&
      requireSelection.tenant &&
      requireSelection.restaurant &&
      !hasValidSelection &&
      !hasProcessedAutoSelection.current; // Only auto-select once

    if (canAutoSelect) {
      logger.debug('Auto-selecting single tenant+restaurant seamlessly');
      hasProcessedAutoSelection.current = true;
      setIsAutoSelecting(true);
      setIsInitialized(true); // Set initialized immediately to prevent re-runs

      const tenant = userTenants[0];
      const locationRecord = locations[0];

      // Set selection immediately. `tenant` is a Firestore DocumentWithId —
      // the origin of a tenant doc id, so the brand is applied here rather
      // than cast at each consumer.
      persistedSelection.setSelection(
        { ...tenant, docId: asTenantDocId(tenant.docId) },
        locationRecord,
      );

      const autoSelectedData = { tenant, restaurant: locationRecord };
      setSelectionData(autoSelectedData);

      if (onSelectionComplete) {
        onSelectionComplete(autoSelectedData);
      }

      // Use a small delay to ensure persistence completes.
      //
      // ⚠️ Held in its OWN ref (see `autoSelectCompleteTimeoutRef`) and
      // cancelled by the unmount-only effect above — deliberately NOT by a
      // cleanup returned from this branch, which would cancel it on the
      // immediate re-run that `setIsAutoSelecting(true)` triggers. Retaining
      // the id alone was not enough: an earlier fix did that and still returned
      // no cleanup here, so the timer kept firing after unmount.
      cancelTimer(autoSelectCompleteTimeoutRef);
      autoSelectCompleteTimeoutRef.current = setTimeout(() => {
        autoSelectCompleteTimeoutRef.current = null;
        setIsAutoSelecting(false);
        setShowWizard(false);
        logger.debug('Seamless auto-selection completed');
      }, 100);

      return;
    }

    // Otherwise initialize normally (only if not already initialized)
    if (!isTenantsLoading && !isInitialized) {
      logger.debug('Initializing with selection state', {
        hasValidSelection,
        persistedTenant: persistedSelection?.selection.tenant?.name,
        persistedRestaurant: persistedSelection?.selection.restaurant?.name,
        requiresTenant: requireSelection.tenant,
        requiresRestaurant: requireSelection.restaurant,
        userTenantsCount: userTenants?.length || 0
      });

      // Check if we'll auto-select (even if restaurants are still loading)
      const willAutoSelect = userTenants?.length === 1 &&
        requireSelection.tenant &&
        requireSelection.restaurant;

      // If we will auto-select, don't show wizard - just keep loading. The
      // watchdog effect above shows the wizard if auto-selection stalls.
      if (willAutoSelect) {
        logger.debug('Will auto-select, keeping loading state');
        setShowWizard(false);
        setIsInitialized(true);
      } else {
        setShowWizard(!hasValidSelection);
        setIsInitialized(true);
      }
    }

    // Check if we should show wizard after restaurants finish loading
    if (isInitialized && !showWizard && !hasValidSelection && !isTenantsLoading && !isLocationsLoading) {
      // If we have one tenant but no restaurants or multiple restaurants, show wizard
      if (userTenants?.length === 1) {
        if (!locations || locations.length === 0) {
          logger.debug('No restaurants found - showing wizard');
          setShowWizard(true);
        } else if (locations.length > 1) {
          logger.debug('Multiple restaurants found - showing wizard');
          setShowWizard(true);
        }
      } else if (userTenants && userTenants.length > 1) {
        logger.debug('Multiple tenants found - showing wizard');
        setShowWizard(true);
      } else if (!userTenants || userTenants.length === 0) {
        logger.debug('No tenants found - showing wizard');
        setShowWizard(true);
      }
    }

    // Deliberately NO cleanup. React runs an effect's cleanup before every
    // re-run, and this effect re-runs on the very state changes its own
    // branches make — a per-run cleanup here is what killed the watchdog, and
    // would kill the completion timer the same way. Each timer is owned by an
    // effect whose lifetime matches it (see above).
  }, [
    // Use length and loading states instead of full objects to reduce re-renders
    userTenants?.length,
    locations?.length,
    isTenantsLoading,
    isLocationsLoading,
    isInitialized,
    isAutoSelecting,
    hasValidSelection,
    supportLandingPending,
    requireSelection.tenant,
    requireSelection.restaurant
  ]);

  // Proactively clear a persisted selection whose tenant the user is not a
  // member of. `hasValidSelection` already refuses to trust it, but clearing the
  // stale localStorage lets the auto-select / wizard path resolve the user's
  // real tenant and overwrite it. Fail-safe: only act once tenants have loaded
  // into a non-empty list (never nuke on a transient empty/error result).
  const clearPersistedSelection = persistedSelection?.clearSelection;
  const persistedHasValidSelection = persistedSelection?.hasValidSelection ?? false;
  // Driven by `/me/locations` rather than the full tenant documents: the
  // tenant-doc query is now disabled whenever the persisted selection is
  // trusted, so keying this off `userTenants` would mean a stale selection is
  // never detected on exactly the path that matters.
  useEffect(() => {
    // THE bug this guard used to cause: during a support landing the rows below
    // are the admin's OWN memberships and the selection is the support tenant,
    // so this fired and deleted it. Hold until the landing settles — by then
    // the landing has evicted those rows and seeded the real scope.
    if (supportLandingPending) return;
    if (isMeLocationsLoading || !Array.isArray(meLocations) || meLocations.length === 0) return;
    if (!persistedHasValidSelection || persistedTenantId === null) return;
    if (meLocations.some((r) => r.tenantId === persistedTenantId)) return;

    logger.warn('Persisted selection tenant is not in the user\'s memberships - clearing', {
      persistedTenantId,
      reachableTenantIds: Array.from(new Set(meLocations.map((r) => r.tenantId))),
    });
    clearPersistedSelection?.();
    hasProcessedAutoSelection.current = false; // allow auto-select to run for the real user
  }, [
    isMeLocationsLoading,
    meLocations,
    persistedHasValidSelection,
    persistedTenantId,
    clearPersistedSelection,
    supportLandingPending,
  ]);

  // Same proactive clear for the restaurant dimension: the tenant belongs to the
  // user but they have no grant reaching the persisted restaurant. Only fires on
  // a PROVEN denial (see persistedRestaurantAccessProvenDenied — fail-open until
  // assignments + both docIds have loaded), so a transient/partial load never
  // nukes a valid selection. Clearing lets auto-select/wizard resolve the user's
  // real restaurant under this tenant.
  useEffect(() => {
    // Same hold as the tenant-dimension clear above, for the same reason.
    if (supportLandingPending) return;
    if (!persistedLocationAccessProvenDenied) return;
    logger.warn('Persisted selection restaurant is not reachable by the user - clearing', {
      persistedTenantDocId,
      persistedRestaurantDocId: persistedLocationDocId,
    });
    clearPersistedSelection?.();
    hasProcessedAutoSelection.current = false; // allow auto-select to run for the real user
  }, [
    persistedLocationAccessProvenDenied,
    persistedTenantDocId,
    persistedLocationDocId,
    clearPersistedSelection,
    supportLandingPending,
  ]);

  // Handle selection completion
  const handleSelectionComplete = (data: SelectionData) => {
    logger.debug('Selection completed', { data });

    setSelectionData(data);
    setShowWizard(false);

    // Persist selection
    if (persistedSelection && data.tenant && data.restaurant) {
      persistedSelection.setSelection(data.tenant as ITenant, data.restaurant as ILocation);
    }

    if (onSelectionComplete) {
      onSelectionComplete(data);
    }
  };

  // Hold the loader while a support landing is establishing its scope. Without
  // this the tab paints the admin's PREVIOUS restaurant (its persisted
  // selection carries their own uid, so every trust check passes) for the
  // second or two the landing takes — and every page mounted underneath fetches
  // against that wrong restaurant before the real selection lands.
  if (supportLandingPending) {
    return <LoadingComponent />;
  }

  // Wait for initialization or show loading during auto-selection
  if (!isInitialized || isAutoSelecting) {
    return <LoadingComponent />;
  }

  // Show loading while waiting for auto-selection to complete
  // Only if we're still loading restaurants and have one tenant
  if (isInitialized && !showWizard && !hasValidSelection) {
    // Still waiting for restaurants to load for auto-selection
    if (isLocationsLoading && userTenants?.length === 1) {
      return <LoadingComponent />;
    }
    // If timeout occurred, wizard should be shown (handled below)
    if (autoSelectTimeout) {
      // Will be handled by showWizard check below
    }
    // Otherwise, show loading (wizard will be shown by useEffect if needed)
    return <LoadingComponent />;
  }

  // Show suspended message when user has no active tenants but has suspended assignments
  if (showWizard && (!userTenants || userTenants.length === 0) && hasSuspendedAssignments) {
    return <SuspendedAccountMessage />;
  }

  // Show selection wizard
  if (showWizard) {
    return (
      <div className="min-h-svh bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <SelectionWizard
            onComplete={handleSelectionComplete}
            updateLastLogin={requireAuth}
            {...selectionWizardProps}
          />
        </div>
      </div>
    );
  }

  // Render children when selection is complete
  if (hasValidSelection) {
    const finalData = selectionData || {
      tenant: persistedSelection?.selection.tenant as ITenant,
      restaurant: persistedSelection?.selection.restaurant as ILocation
    };

    if (typeof children === 'function') {
      return <>{children(finalData)}</>;
    }
    return <>{children}</>;
  }

  // Fallback loading state
  return <LoadingComponent />;
}
